import { PerformanceAudienceTrendPoint } from '@/services/endpoints/performanceAudience';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usePerformanceAudienceFormatters } from '../../hooks/usePerformanceAudienceFormatters';
import { PLATFORM_CHART_COLORS } from '../../constants';

type PlatformTrendChartProps = {
  data: PerformanceAudienceTrendPoint[];
  isPercentage?: boolean;
};

const PlatformTrendTooltip: FC<{
  active?: boolean;
  payload?: { payload: PerformanceAudienceTrendPoint }[];
  isPercentage?: boolean;
}> = ({ active, payload, isPercentage }) => {
  const { t, i18n } = useTranslation('performanceAudience');
  const { formatNumber } = usePerformanceAudienceFormatters();

  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0].payload;
  const formatValue = (value: number) => {
    if (!isPercentage) {
      return formatNumber(value);
    }

    const formatted = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
    return `${formatted}%`;
  };

  return (
    <div className='rounded-lg border border-ink-200 bg-white p-3 text-xs shadow-md'>
      <p className='mb-2 font-semibold text-ink-900'>{point.label}</p>
      <p style={{ color: PLATFORM_CHART_COLORS.google }}>
        {t('platforms.google')}: {formatValue(point.google)}
      </p>
      <p style={{ color: PLATFORM_CHART_COLORS.meta }}>
        {t('platforms.meta')}: {formatValue(point.meta)}
      </p>
    </div>
  );
};

export const PlatformTrendChart: FC<PlatformTrendChartProps> = ({ data, isPercentage }) => {
  const { t } = useTranslation('performanceAudience');

  return (
    <div className='flex flex-col gap-2'>
      <div className='mb-1.5 flex flex-wrap justify-start gap-x-[18px] gap-y-1.5'>
        <span className='flex items-center gap-2 text-[13.5px] text-ink-800'>
          <span className='inline-block h-[3px] w-[18px] rounded-sm' style={{ background: PLATFORM_CHART_COLORS.google }} />
          {t('platforms.googleAds')}
        </span>
        <span className='flex items-center gap-2 text-[13.5px] text-ink-800'>
          <span
            className='inline-block h-[3px] w-[18px] rounded-sm'
            style={{ backgroundImage: 'repeating-linear-gradient(90deg,var(--ink-900) 0 4px,transparent 4px 7px)' }}
          />
          {t('platforms.meta')}
        </span>
      </div>
      <ResponsiveContainer width='100%' height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id='googleAreaGradient' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={PLATFORM_CHART_COLORS.google} stopOpacity={0.18} />
              <stop offset='100%' stopColor={PLATFORM_CHART_COLORS.google} stopOpacity={0} />
            </linearGradient>
            <linearGradient id='metaAreaGradient' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={PLATFORM_CHART_COLORS.meta} stopOpacity={0.14} />
              <stop offset='100%' stopColor={PLATFORM_CHART_COLORS.meta} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray='none' stroke='#EFF1F5' horizontal vertical={false} />
          <XAxis dataKey='label' axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#7A8597' }} dy={8} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#9CA3B1' }}
            tickFormatter={(value) => (isPercentage ? `${value}%` : value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
            width={48}
          />
          <Tooltip content={<PlatformTrendTooltip isPercentage={isPercentage} />} />
          <Area type='monotone' dataKey='google' fill='url(#googleAreaGradient)' stroke='none' />
          <Area type='monotone' dataKey='meta' fill='url(#metaAreaGradient)' stroke='none' />
          <Line
            type='monotone'
            dataKey='google'
            stroke={PLATFORM_CHART_COLORS.google}
            strokeWidth={2.4}
            dot={false}
            activeDot={{ r: 4, fill: PLATFORM_CHART_COLORS.google, stroke: '#fff', strokeWidth: 2 }}
          />
          <Line
            type='monotone'
            dataKey='meta'
            stroke={PLATFORM_CHART_COLORS.meta}
            strokeWidth={2.4}
            strokeDasharray='5 4'
            dot={false}
            activeDot={{ r: 4, fill: PLATFORM_CHART_COLORS.meta, stroke: '#fff', strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
